import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { needsPartnerAgentTerms } from "utilities/userType";
import PartnerAgentTermsModal from "components/PartnerAgentTermsModal";
import { FEATURE_OPTIONS } from "constants/companies";
import {
  CREATE_TYPE_META,
  canCreateType,
  visibleFieldsFor,
  tierOptionsForCreateType,
  emptyCreateForm,
  validateCreateForm,
  buildCreatePayload,
} from "utilities/companyCreate";
import { tierAfterParentChange, tierDefaultHint } from "utilities/tierInheritance";
import {
  Building2,
  X,
  Search,
  Check,
  Link as LinkIcon,
  Unlink,
} from "lucide-react";

/**
 * The one create-a-company modal (SOUND-296). Replaces `CreateClientModal`
 * and `CreateParentCompanyModal`.
 *
 * Everything that varies by `type` — which fields render, which tier
 * vocabulary they offer, and the exact request body — comes from
 * `utilities/companyCreate`, which is pure and unit-tested. This file is
 * rendering and network only. If you need to change WHAT gets sent, change it
 * there so the test moves with it.
 *
 * Preserved verbatim from the two modals this replaces:
 *  - the partner T&C gate (client-side `needsPartnerAgentTerms` pre-check plus
 *    the backend 403 `PARTNER_TERMS_REQUIRED` safety net, both retrying the
 *    create on accept),
 *  - the 409 → inline field error rather than a toast,
 *  - the internal-only parent picker.
 *
 * @param {{ type: "client"|"parent"|"partner", onClose: Function, onCreated: Function }} props
 */
export default function CreateCompanyModal({ type, onClose, onCreated }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const meta = CREATE_TYPE_META[type];
  // Defence in depth behind CreateCompanyMenu: a partner user can never be in
  // the parent or partner form, however this modal got opened.
  const allowed = canCreateType(auth?.user, type);

  const fields = new Set(visibleFieldsFor(type, auth?.user));
  const tierOptions = tierOptionsForCreateType(type);

  const [saving, setSaving] = useState(false);
  const [conflictError, setConflictError] = useState("");
  const [nameError, setNameError] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const [form, setForm] = useState(emptyCreateForm);
  const [pickerOpen, setPickerOpen] = useState(false);
  // SOUND-795: true while `form.tier` holds the value defaulted from a
  // Managed Services parent (drives the hint; any manual pick clears it).
  const [tierDefaulted, setTierDefaulted] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "tier") setTierDefaulted(false);
    if (field === "name") setNameError("");
    if (field === "name" || field === "slug") setConflictError("");
  };

  // Choosing or clearing the parent may default the tier (SOUND-795). The
  // decision is in utilities/tierInheritance (tested); this only applies it.
  const handleParentChange = (parent) => {
    const next = tierAfterParentChange({ tier: form.tier, parent, tierDefaulted });
    setForm((prev) => ({ ...prev, parentCompany: parent, tier: next.tier }));
    setTierDefaulted(next.tierDefaulted);
  };

  const tierHint = tierDefaultHint({ tierDefaulted, tier: form.tier });

  const handleFeatureToggle = (feature) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  // A 409 is a name-or-slug collision. Point at the slug when the user typed
  // one — that is the field they can actually fix; otherwise the slug was
  // derived from the name, so the name is the culprit.
  const conflictField =
    fields.has("slug") && form.slug.trim() ? "slug" : "name";

  const submitCreate = async () => {
    const invalid = validateCreateForm(form);
    if (invalid) {
      setNameError(invalid.message);
      return;
    }

    const payload = buildCreatePayload({ type, form, user: auth?.user });

    setSaving(true);
    setNameError("");
    setConflictError("");
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.createCompany,
        auth.user,
        effectiveCompanyId,
        payload
      );

      // Backend contract (SOUND-276): { company, application? } for a client
      // create; a bare company document for the others.
      const body = res?.data || {};
      const created = body.company || body || {};

      toast({
        status: "success",
        title: meta.successTitle,
        description: created.name ? `"${created.name}" is ready.` : undefined,
      });
      onCreated?.(created);
    } catch (err) {
      const status = err.response?.status;
      const serverError = err.response?.data?.error;

      if (status === 403 && serverError === "PARTNER_TERMS_REQUIRED") {
        // Backend safety-net gate: open the T&C modal and retry on accept.
        setTermsOpen(true);
        return;
      }

      console.error("[CreateCompanyModal] Create failed", err);
      if (status === 409) {
        setConflictError(serverError || "That name or slug is already taken");
      } else {
        toast({
          status: "destructive",
          title: `Failed to create ${meta.label.toLowerCase()}`,
          description: serverError || "Unknown error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Client-side T&C gate for partners. Internal users never trip it.
    if (needsPartnerAgentTerms(auth.user)) {
      setTermsOpen(true);
      return;
    }
    await submitCreate();
  };

  if (!meta || !allowed) return null;

  const nameMessage =
    nameError || (conflictField === "name" ? conflictError : "");
  const slugMessage = conflictField === "slug" ? conflictError : "";

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={saving ? undefined : onClose}
      >
        <div
          className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand/20 rounded-lg">
                <Building2 className="w-5 h-5 text-brand" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{meta.modalTitle}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {meta.modalSubtitle}
                </p>
              </div>
            </div>
            <button
              className="btn btn-ghost h-8 w-8 p-0"
              onClick={onClose}
              aria-label="Close"
              disabled={saving}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Name — required on every type */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                className={`input input-bordered w-full ${
                  nameMessage ? "border-destructive ring-destructive" : ""
                }`}
                placeholder={meta.namePlaceholder}
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                autoFocus
              />
              {nameMessage ? (
                <p className="text-xs text-destructive mt-1">{nameMessage}</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  The common name you know this {meta.label.toLowerCase()} by.
                </p>
              )}
            </div>

            {fields.has("slug") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  className={`input input-bordered w-full ${
                    slugMessage ? "border-destructive ring-destructive" : ""
                  }`}
                  placeholder="e.g. stallone-holdings (optional)"
                  value={form.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                />
                {slugMessage ? (
                  <p className="text-xs text-destructive mt-1">{slugMessage}</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Human-readable unique identifier. Leave empty to derive it
                    from the name.
                  </p>
                )}
              </div>
            )}

            {fields.has("legalName") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Legal Name
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="e.g. Acme Widgets, LLC"
                  value={form.legalName}
                  onChange={(e) => handleChange("legalName", e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Pre-fills the Registration tab. You can finish the
                  rest of the form later.
                </p>
              </div>
            )}

            {fields.has("businessEmail") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Business Email
                </label>
                <input
                  type="email"
                  className="input input-bordered w-full"
                  placeholder="contact@client.com"
                  value={form.businessEmail}
                  onChange={(e) => handleChange("businessEmail", e.target.value)}
                />
              </div>
            )}

            {fields.has("tier") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Tier
                </label>
                <select
                  className="input input-bordered w-full"
                  value={form.tier}
                  onChange={(e) => handleChange("tier", e.target.value)}
                >
                  <option value="">Select tier (optional)…</option>
                  {tierOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                {tierHint && (
                  <p className="text-xs text-muted-foreground mt-1">{tierHint}</p>
                )}
              </div>
            )}

            {fields.has("features") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Features
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_OPTIONS.map((feature) => (
                    <label
                      key={feature}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.features.includes(feature)}
                        onChange={() => handleFeatureToggle(feature)}
                        className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                      />
                      {feature}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {fields.has("parentCompany") && (
              <div>
                <label className="text-sm font-medium text-foreground block mb-1">
                  Parent Company
                </label>
                {form.parentCompany ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-4 w-4 text-brand shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {form.parentCompany.name}
                        </div>
                        {form.parentCompany.slug && (
                          <div className="text-xs text-muted-foreground font-mono truncate">
                            {form.parentCompany.slug}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleParentChange(null)}
                      title="Remove parent link"
                    >
                      <Unlink className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline w-full"
                    onClick={() => setPickerOpen(true)}
                  >
                    <LinkIcon className="h-4 w-4" />
                    Link to a parent (optional)
                  </button>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Internal-only. Links this new client under an existing parent
                  client at creation time.
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                className="btn btn-ghost flex-1"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary flex-1"
                onClick={handleSubmit}
                disabled={saving || !form.name.trim()}
              >
                {saving ? "Creating…" : meta.submitLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <ParentPicker
          onClose={() => setPickerOpen(false)}
          onSelect={(c) => {
            handleParentChange(c);
            setPickerOpen(false);
          }}
        />
      )}

      <PartnerAgentTermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={submitCreate}
      />
    </>
  );
}

/**
 * Minimal inline picker mirroring `LinkParentCompanyModal`'s search/select
 * flow, but without persisting a link — we simply hand the selected company
 * back to the caller so it can include `parentCompanyId` in the create
 * payload. Carried across unchanged from `CreateClientModal`.
 *
 * Module-level on purpose: a component declared inside a render body is a new
 * TYPE on every render, so React remounts it and the search input loses focus
 * on the first keystroke.
 */
function ParentPicker({ onClose, onSelect }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCandidates(query), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const fetchCandidates = async (search) => {
    if (!effectiveCompanyId || !auth?.user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("companyType", "client");
      params.set("limit", "25");
      if (search) params.set("search", search);

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.getCompanies}?${params.toString()}`,
        auth.user,
        effectiveCompanyId
      );
      const all = Array.isArray(res?.data?.data) ? res.data.data : [];
      // Same filter rules as LinkParentCompanyModal (no sound-admin, no
      // already-child, no non-client).
      const filtered = all.filter((c) => {
        if (!c) return false;
        if (c.parentCompanyId) return false;
        if (c.companyId === "sound-admin" || c.slug === "sound-admin") return false;
        if (c.companyType && c.companyType !== "client") return false;
        return true;
      });
      setResults(filtered);
    } catch (err) {
      console.error("[CreateCompanyModal.ParentPicker] Search failed", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold">Link to Parent Company</h3>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            className="input input-bordered w-full pl-9"
            placeholder="Search by name, slug, or company ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-72 overflow-y-auto -mx-2 px-2 mb-3">
          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {query ? "No eligible companies found." : "Start typing to search."}
            </div>
          ) : (
            <ul className="space-y-1">
              {results.map((c) => {
                const isSelected = selected?.companyId === c.companyId;
                return (
                  <li key={c.companyId}>
                    <button
                      type="button"
                      onClick={() => setSelected(c)}
                      className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-brand bg-brand/10"
                          : "border-transparent hover:border-border hover:bg-muted/60"
                      }`}
                    >
                      <div
                        className={`p-1.5 rounded-md ${
                          isSelected ? "bg-brand/20" : "bg-muted"
                        }`}
                      >
                        <Building2
                          className={`h-4 w-4 ${
                            isSelected ? "text-brand" : "text-muted-foreground"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {c.name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.slug ? `${c.slug} · ` : ""}
                          {c.companyId}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="h-4 w-4 text-brand shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          <button className="btn btn-ghost flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={() => selected && onSelect(selected)}
            disabled={!selected}
          >
            {selected ? `Use ${selected.name}` : "Select a parent"}
          </button>
        </div>
      </div>
    </div>
  );
}
